import {
  ByceFacilitatorOverview,
  ByceParticipantEvidence,
  byceMemberColumns,
} from "./ByceFacilitatorEvidence";
import { BYCE_PROGRAM_SLUG } from "./byceEvidencePresentation";
import {
  ByceSessionReviewFacilitatorOverview,
  ByceSessionReviewParticipantEvidence,
  byceSessionReviewMemberColumns,
} from "./ByceSessionReviewFacilitatorEvidence";
import { ByceG3FacilitatorOverview, ByceG3ParticipantEvidence, byceG3MemberColumns } from "./ByceG3FacilitatorEvidence";
import { BYCE_KSU_PROGRAM_IDENTITY, normalizeByceProgramIdentity } from "../../engine/byceKsuIdentity";

const isText = value => typeof value === "string" && value.trim().length > 0;

export const createProgramFacilitatePresentationAdapterRegistry = (registrations = []) => {
  const adapters = registrations.map(registration => {
    if (!isText(registration?.key) || !isText(registration?.programSlug) ||
        !Array.isArray(registration?.programVersions) || !registration.programVersions.length) {
      throw new TypeError("Facilitate presentation adapters require a key and exact Program compatibility");
    }
    return Object.freeze({ ...registration, programVersions: Object.freeze([...registration.programVersions]) });
  });
  return Object.freeze({
    resolve(program) {
      if (!program) return null;
      program = normalizeByceProgramIdentity(program);
      return adapters.find(adapter =>
        adapter.programSlug === program.slug && adapter.programVersions.includes(program.version)) || null;
    },
    list: () => Object.freeze([...adapters]),
  });
};

export const byceFacilitatePresentationAdapter = Object.freeze({
  key: "building-your-clinical-ear.facilitate-presentation@1",
  programSlug: BYCE_PROGRAM_SLUG,
  programVersions: Object.freeze(["0.1-collaborator-review"]),
  OverviewComponent: ByceFacilitatorOverview,
  ParticipantEvidenceComponent: ByceParticipantEvidence,
  createMemberColumns: byceMemberColumns,
  overviewSectionTitle: "Cohort overview",
  configuredProgress: Object.freeze({
    secondary: true,
    summary: "Configured step-completion state",
    description: "Secondary configured-step state; BYCE educational progress is shown above.",
  }),
  suppressDefaultAttemptDetails: true,
});

export const byceSessionReviewFacilitatePresentationAdapter = Object.freeze({
  key: "building-your-clinical-ear-session-review.facilitate-presentation@1",
  programSlug: "building-your-clinical-ear-session-review-program",
  programVersions: Object.freeze(["0.1-structural-review"]),
  OverviewComponent: ByceSessionReviewFacilitatorOverview,
  ParticipantEvidenceComponent: ByceSessionReviewParticipantEvidence,
  createMemberColumns: byceSessionReviewMemberColumns,
  membersDescription: "Review participant progress across Stages and Listening Sessions.",
  overviewSectionTitle: "Session progress overview",
  configuredProgress: Object.freeze({
    hidden: true,
  }),
  suppressDefaultAttemptDetails: true,
});

export const byceG3FacilitatePresentationAdapter = Object.freeze({
  key: "ksu-building-your-clinical-ear.facilitate-presentation@1",
  programSlug: BYCE_KSU_PROGRAM_IDENTITY.slug,
  programVersions: Object.freeze([BYCE_KSU_PROGRAM_IDENTITY.version]),
  OverviewComponent: ByceG3FacilitatorOverview,
  ParticipantEvidenceComponent: ByceG3ParticipantEvidence,
  createMemberColumns: byceG3MemberColumns,
  membersDescription: "Review participant progress across G3 Stages, Listening Sessions, Reflections, and the final rating.",
  overviewSectionTitle: "Session progress overview",
  configuredProgress: Object.freeze({ hidden: true }),
  suppressProjectContext: true,
  suppressDefaultAttemptDetails: true,
});

export const programFacilitatePresentationAdapterRegistry =
  createProgramFacilitatePresentationAdapterRegistry([
    byceFacilitatePresentationAdapter,
    byceSessionReviewFacilitatePresentationAdapter,
    byceG3FacilitatePresentationAdapter,
  ]);

export const resolveProgramFacilitatePresentationAdapter = program =>
  programFacilitatePresentationAdapterRegistry.resolve(program);
